import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { Checkbox } from '@strata/react-checkbox-list';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { Dialog } from '@strata/react-dialog';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import PropTypes from 'prop-types';

import { handleError } from 'services/Error';
import {
    useUnwatchMutation,
    useUpsertWatchMutation,
    WatchResources,
} from 'store/api/notificationApi';

const Title = 'Notification Settings';

const CLAIM_WATCH_EVENTS = [
    { value: 'paid', label: 'Paid', topic: 'treatmentClaimPaid' },
    { value: 'rejected', label: 'Rejected', topic: 'treatmentClaimRejected' },
    { value: 'discrepancy', label: 'Discrepancy', topic: 'treatmentClaimDiscrepancy' },
    { value: 'reversed', label: 'Reversed', topic: 'treatmentClaimReversed' },
];

const getInitialClaimEvents = (existingWatch) => {
    if (!existingWatch) {
        return CLAIM_WATCH_EVENTS.map((event) => event.value);
    }
    return CLAIM_WATCH_EVENTS.filter((event) =>
        existingWatch.kafkaTopics?.includes(event.topic),
    ).map((event) => event.value);
};

const claimEventsToKafkaTopics = (claimEvents) =>
    CLAIM_WATCH_EVENTS.filter((event) => claimEvents.includes(event.value)).map(
        (event) => event.topic,
    );

const stopHeaderToggle = (event) => {
    event.stopPropagation();
};

export const NotificationSettingsDialog = ({
    isOpen,
    onClose,
    treatmentDetails,
    existingWatch = undefined,
}) => {
    const [upsertWatch] = useUpsertWatchMutation();
    const [unwatch] = useUnwatchMutation();

    const { treatmentId, treatmentCohortId, payerId, firstName, lastName, productServiceOid } =
        treatmentDetails ?? {};

    const handleStopWatching = async (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!existingWatch?.watchId) {
            return;
        }
        try {
            await unwatch(existingWatch.watchId).unwrap();
            SuccessToast({ message: 'Stopped watching this treatment.' });
            onClose();
        } catch (err) {
            handleError('Unable to stop watching. Please try again.', err);
        }
    };

    return (
        <Formik
            initialValues={{ claimEvents: getInitialClaimEvents(existingWatch) }}
            enableReinitialize
            onSubmit={async (values, { resetForm }) => {
                try {
                    await upsertWatch({
                        resource: WatchResources.TREATMENT,
                        resource_id: treatmentId,
                        cohort_id: treatmentCohortId ?? null,
                        kafka_topics: claimEventsToKafkaTopics(values.claimEvents),
                        patient_first_name: firstName ?? null,
                        patient_last_name: lastName ?? null,
                        product_service_oid: productServiceOid ?? null,
                        payer_id: payerId ?? null,
                    }).unwrap();
                    SuccessToast({ message: 'Notification settings saved.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to save notification settings. Please try again.', err);
                }
            }}
        >
            {(formik) => {
                const selectedCount = formik.values.claimEvents.length;
                const allSelected = selectedCount === CLAIM_WATCH_EVENTS.length;
                const someSelected = selectedCount > 0 && !allSelected;

                return (
                    <Dialog
                        dialogTitle={Title}
                        headerIcon={IconNames.NOTIFICATIONS}
                        width={560}
                        isOpen={isOpen}
                        portalClassName="sds_notification-settings-dialog__portal"
                        secondaryButtonLabel="Cancel"
                        onSecondaryButtonClick={() => {
                            onClose();
                            formik.resetForm();
                        }}
                        primaryButtonLabel="Save"
                        primaryButtonType="button"
                        onPrimaryButtonClick={() => formik.submitForm()}
                        isPrimaryButtonDisabled={formik.isSubmitting}
                        additionalButtonsLeft={
                            existingWatch?.watchId ? (
                                <ActionButtonText
                                    label="Stop Watching"
                                    leftIcon={IconNames.EYE_OFF}
                                    onClick={handleStopWatching}
                                    disabled={formik.isSubmitting}
                                    className="sds_notification-settings-dialog__stop-watching"
                                />
                            ) : undefined
                        }
                        dialogContent={
                            <div
                                id="notification-settings-dialog"
                                className="sds_notification-settings-dialog"
                            >
                                <p className="margin__bottom--small">
                                    This treatment will appear in the Watching section of the
                                    notifications panel until you stop watching it.
                                </p>
                                <p className="margin__bottom--large">
                                    You may also select specific notification events below.
                                </p>
                                <div className="sds_notification-settings-dialog__claims margin__bottom--large">
                                    {/* Keeps block width stable when Claims is collapsed (events unmount from layout). */}
                                    <div
                                        className="sds_notification-settings-dialog__width-anchor sds_notification-settings-dialog__grid"
                                        aria-hidden="true"
                                    >
                                        <span className="padding__left--large">Discrepancy</span>
                                        <Checkbox checked disabled />
                                    </div>
                                    <div className="sds_notification-settings-dialog__channel-row">
                                        <div className="sds_notification-settings-dialog__grid">
                                            <span
                                                className="sds_notification-settings-dialog__col1-sizer padding__left--large"
                                                aria-hidden="true"
                                            >
                                                Discrepancy
                                            </span>
                                            <div className="sds_notification-settings-dialog__channel-header font-semi-bold">
                                                <Icon
                                                    icon={IconNames.NOTIFICATIONS}
                                                    size={16}
                                                    title="In-app notification"
                                                />
                                                <span>In-app notification</span>
                                            </div>
                                        </div>
                                    </div>
                                    <CollapsibleSection
                                        className="sds_notification-settings-dialog__claims-section"
                                        title="Claims"
                                        chevronSize={CollapsibleSection.ChevronSize.SMALL}
                                        rightHeaderContent={
                                            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                                            <span
                                                className="sds_notification-settings-dialog__select-all"
                                                onClick={stopHeaderToggle}
                                                onKeyDown={stopHeaderToggle}
                                            >
                                                <Checkbox
                                                    checked={allSelected}
                                                    indeterminate={someSelected}
                                                    onChange={(isChecked) => {
                                                        formik.setFieldValue(
                                                            'claimEvents',
                                                            isChecked
                                                                ? CLAIM_WATCH_EVENTS.map(
                                                                      (event) => event.value,
                                                                  )
                                                                : [],
                                                        );
                                                    }}
                                                />
                                            </span>
                                        }
                                        content={
                                            <div className="sds_notification-settings-dialog__grid">
                                                {CLAIM_WATCH_EVENTS.map((event) => (
                                                    <div
                                                        key={event.value}
                                                        className="sds_notification-settings-dialog__row"
                                                    >
                                                        <span className="padding__left--large">
                                                            {event.label}
                                                        </span>
                                                        <Checkbox
                                                            checked={formik.values.claimEvents.includes(
                                                                event.value,
                                                            )}
                                                            onChange={(isChecked) => {
                                                                formik.setFieldValue(
                                                                    'claimEvents',
                                                                    isChecked
                                                                        ? [
                                                                              ...formik.values
                                                                                  .claimEvents,
                                                                              event.value,
                                                                          ]
                                                                        : formik.values.claimEvents.filter(
                                                                              (value) =>
                                                                                  value !==
                                                                                  event.value,
                                                                          ),
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                    />
                                </div>
                            </div>
                        }
                    />
                );
            }}
        </Formik>
    );
};

NotificationSettingsDialog.Title = Title;

NotificationSettingsDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        treatmentId: PropTypes.string.isRequired,
        treatmentCohortId: PropTypes.string,
        payerId: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        productServiceOid: PropTypes.string,
    }).isRequired,
    existingWatch: PropTypes.shape({
        watchId: PropTypes.string.isRequired,
        kafkaTopics: PropTypes.arrayOf(PropTypes.string),
    }),
};
